import { requiredString } from "@frontend-shared/schema/field-builders";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { getTestIdWithPrefix } from "@/lib/utils";
import { FORM_VALIDATION_OPTIONS } from "@/lib/validations/form-defaults";

import { ConfirmationDialogBody } from "./ConfirmationDialogBody";
import { FormSubmitButton } from "./FormSubmitButton";
import { StyledInput } from "./StyledInput";
import type {
  IDeleteConfirmationDialogProps,
  IDeleteConfirmationFieldRenderProps,
  IDeleteConfirmationFormValues,
} from "./types";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
export const DeleteConfirmationDialog = (
  props: IDeleteConfirmationDialogProps,
): JSX.Element => {
  const { t } = useTranslation("common");
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const {
    open: isOpen,
    isDeleting,
    onOpenChange,
    testIdPrefix,
    content,
    handlers,
    step1Submit: legacyStep1Submit,
    step2Submit: legacyStep2Submit,
    step1Title: legacyStep1Title,
    step1Description: legacyStep1Description,
    step2Title: legacyStep2Title,
    step2Description: legacyStep2Description,
    step1ConfirmVariant,
    confirmButtonText: legacyConfirmButtonText,
    cancelButtonText: legacyCancelButtonText,
    inputLabel: legacyInputLabel,
    inputPlaceholder: legacyInputPlaceholder,
  } = props;

  const step1Title = content?.step1?.title ?? legacyStep1Title;
  const step1Description =
    content?.step1?.description ?? legacyStep1Description;
  const step2Title = content?.step2?.title ?? legacyStep2Title;
  const step2Description =
    content?.step2?.description ?? legacyStep2Description;
  const inputLabel = content?.inputLabel ?? legacyInputLabel;
  const inputPlaceholder = content?.inputPlaceholder ?? legacyInputPlaceholder;
  const step1Submit = handlers?.onStep1Submit ?? legacyStep1Submit;
  const step2Submit = handlers?.onStep2Submit ?? legacyStep2Submit;

  const confirmText =
    content?.confirmButtonText ??
    legacyConfirmButtonText ??
    t("form_buttons.delete");
  const cancelText =
    content?.cancelButtonText ??
    legacyCancelButtonText ??
    t("form_buttons.cancel");

  const confirmationSchema = z.object({
    confirmationText: requiredString({
      requiredMessage: t("delete_confirmation_dialog.value_required", {
        name: inputLabel,
      }),
    }),
  });

  const form = useForm<IDeleteConfirmationFormValues>({
    ...FORM_VALIDATION_OPTIONS,
    resolver: zodResolver(confirmationSchema),
    defaultValues: {
      confirmationText: "",
    },
  });

  const handleOpenChange = useCallback(
    (nextOpen: boolean): void => {
      onOpenChange?.(nextOpen);
      if (!nextOpen) {
        setCurrentStep(1);
        form.reset({ confirmationText: "" });
      }
    },
    [form, onOpenChange],
  );

  const cancelRef = useRef<HTMLButtonElement>(null);
  const renderConfirmationField = useCallback(
    (fieldRenderProps: IDeleteConfirmationFieldRenderProps): JSX.Element => {
      const { field, fieldState } = fieldRenderProps;
      const { error } = fieldState;

      return (
        <FormItem className="mb-4">
          <FormLabel>{inputLabel}</FormLabel>
          <FormControl>
            <StyledInput
              {...field}
              isError={!!error}
              placeholder={inputPlaceholder}
              data-testid={getTestIdWithPrefix(
                "delete-confirmation-dialog-input",
                testIdPrefix,
              )}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      );
    },
    [inputLabel, inputPlaceholder, testIdPrefix],
  );

  const onStep2FormSubmit = useCallback(
    (values: IDeleteConfirmationFormValues): void => {
      step2Submit?.(values.confirmationText);
    },
    [step2Submit],
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        autoFocus={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          cancelRef.current?.focus();
        }}
      >
        {currentStep === 1 && (
          <ConfirmationDialogBody
            content={{
              title: step1Title,
              description: step1Description,
              cancelText,
              confirmText,
            }}
            actions={{
              onCancel: () => handleOpenChange(false),
              onConfirm: () => step1Submit?.(() => setCurrentStep(2)),
              confirmVariant: step1ConfirmVariant ?? "destructive",
              confirmDisabled: isDeleting,
              confirmLoading: isDeleting,
            }}
            cancelRef={cancelRef}
            testIdPrefix={getTestIdWithPrefix(
              "delete-confirmation-dialog-step1",
              testIdPrefix,
            )}
            classNames={{
              cancelButton: "grow",
              confirmButton: "grow",
            }}
          />
        )}
        {currentStep === 2 && (
          <>
            <DialogHeader>
              <DialogTitle
                className="text-center text-2xl mb-2"
                data-testid={getTestIdWithPrefix(
                  "delete-confirmation-dialog-step2-title",
                  testIdPrefix,
                )}
              >
                {step2Title}
              </DialogTitle>
              <DialogDescription
                className="text-center text-base"
                data-testid={getTestIdWithPrefix(
                  "delete-confirmation-dialog-step2-description",
                  testIdPrefix,
                )}
              >
                {step2Description}
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onStep2FormSubmit)}>
                <FormField
                  control={form.control}
                  name="confirmationText"
                  render={renderConfirmationField}
                />
                <DialogFooter className="flex items-center mt-2">
                  <DialogClose asChild>
                    <Button
                      variant="outline"
                      className="grow"
                      data-testid={getTestIdWithPrefix(
                        "delete-confirmation-dialog-step2-cancel-button",
                        testIdPrefix,
                      )}
                    >
                      {cancelText}
                    </Button>
                  </DialogClose>
                  <FormSubmitButton
                    isLoading={isDeleting}
                    disabled={isDeleting}
                    variant="destructive"
                    className="grow"
                    data-testid={getTestIdWithPrefix(
                      "delete-confirmation-dialog-step2-submit-button",
                      testIdPrefix,
                    )}
                  >
                    {confirmText}
                  </FormSubmitButton>
                </DialogFooter>
              </form>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
